import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Portal } from 'src/app/models/portal.model';
import { PortalMember } from 'src/app/models/portalMember.model';
import { AppUser } from 'src/app/models/appUser.model';
import { PortalInvite } from 'src/app/models/portalInvite.model';
import { PortalRole } from 'src/app/models/portalRole.model';
import { StorageService } from '../storage/storage.service';
import { Theme } from 'src/app/models/theme.model';
import { ThemeService } from '../theme/theme.service';
import { AppDevice } from 'src/app/models/appDevice.model';
import { AppSettingsService } from '../app-settings/app-settings.service';
import * as moment from 'moment';

export class Member {
  constructor(newAppUser: AppUser, newPortalMember: PortalMember, newAppDevice: AppDevice) {
    this.appUser = newAppUser;
    this.portalMember = newPortalMember;
    this.appDevice = newAppDevice;

    this.selectable = false;
  }

  appUser: AppUser;
  portalMember: PortalMember;
  appDevice: AppDevice;
  selectable: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PortalService {
  portal: BehaviorSubject<[(Portal|null), number]> = new BehaviorSubject<[(Portal|null), number]>([null, moment().valueOf()]);

  portalRules: BehaviorSubject<{ [key: number]: any }> = new BehaviorSubject<{ [key: number]: any }>({});

  portals: { [key: number]: Portal } = [];

  private httpManual: HttpClient;

  constructor(
    private http: HttpClient,
    private handler: HttpBackend,
    private storage: StorageService,
    private themeService: ThemeService,
    private settings: AppSettingsService,
  ) {
    this.httpManual = new HttpClient(this.handler);
  }

  getPortal(portalID: number): Promise<Portal> {
    return this.storage.get("portal").then((portal: Portal) => {
      if(portal && portal.id) {
        let newPortal: Portal = new Portal();
        newPortal.populate(portal);

        newPortal.theme = new Theme();
        newPortal.theme.populate(portal.theme);
        return newPortal;
      } else {
        const url = this.settings.apiUrl + `portals/${portalID}`;

        let observable: Observable<Portal> = this.http.get(url).pipe(
          map((data: any) => {
            if(data) {
              let newPortal = new Portal(data.portal);
              newPortal.theme = new Theme(data.theme);
              this.storage.set("portal", newPortal);
              return newPortal;
            }
          })
        );
        return observable.toPromise();
      }
    });
  }

  getPortalFromStorage() : Promise<Portal> {
    return this.storage.get("portal").then((portal: Portal) => {
      if(portal && portal.id) {
        let newPortal: Portal = new Portal();
        newPortal.populate(portal);

        newPortal.theme = new Theme();
        newPortal.theme.populate(portal.theme);
        return newPortal;
      } else {
        return null;
      }
    });
  }

  setReturnPortal(portal?: Portal) : void {
    if(portal) {
      this.storage.set('returnPortal', portal);
    } else {
      this.storage.remove('returnPortal');
    }
  }

  returnToPortal(timePageLoaded: number) : void {
    this.storage.get("returnPortal").then((portal: Portal) => {
      if (portal) {
        this.storage.remove('returnPortal');
        this.storage.set("portal", portal);
        this.themeService.setTheme(portal.theme, timePageLoaded);
        this.portal.next([portal, timePageLoaded]);
      } else {
        this.storage.remove("portal");
        this.portal.next(null);
      }
    });
  }

  getPortalRules(portalID: number): Observable<any> {
    // Check if the portalID is in the dictionary
    let currentPortalRules = this.portalRules.getValue();
    if (portalID in currentPortalRules) {
      return of(currentPortalRules[portalID]);
    } else {
      // Add a blank value to our dictionary to stop multiple api requests for the same portal
      currentPortalRules[portalID] = null;
      this.portalRules.next(currentPortalRules);

      const url = this.settings.apiUrl + `portals/${portalID}/screenrules`;

      return this.http.get(url).pipe(
        map((data: any) => {
          currentPortalRules[portalID] = data.portal_rules;
          this.portalRules.next(currentPortalRules);
          return data.portal_rules;
        })
      );
    }
  }

  getPortalByPortalID(portalID: number): Observable<Portal> {
    if (portalID in this.portals) {
      return of(this.portals[portalID]);
    } else {
      const url = this.settings.apiUrl + `portals/${portalID}`;

      return this.http.get(url).pipe(
        map((data: any) => {
          let portal: Portal = new Portal(data.portal);
          portal.theme = new Theme(data.theme);
          this.portals[portalID] = portal;
          // this.storage.set("portal", portal); // keep this 
          return portal;
        })
      );
    }
  }

  getPortals(): Observable<Portal[]> {
    const url = this.settings.apiUrl + `portals`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let portals: Portal[] = [];
        if (data) {
          data.forEach((portal: any) => {
            let newPortal: Portal = new Portal(portal.portal);
            newPortal.theme = new Theme(portal.theme);
            portals.push(newPortal);
          });
        }
        return portals;
      })
    );
  }

  getStaffPortals(): Observable<Portal[]> {
    const params = {
      is_staff: true,
    }

    const url = this.settings.apiUrl + `portals?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let portals: Portal[] = [];
        data.forEach((portal: any) => {
          let newPortal: Portal = new Portal(portal.portal);
          newPortal.theme = new Theme(portal.theme);
          portals.push(newPortal);
        });
        return portals;
      })
    );
  }

  getChildrenPortals(portalID: number): Observable<Portal[]> {
    const url = this.settings.apiUrl + `portals/${portalID}/children`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let portals: Portal[] = [];
        if(data) {
          data.forEach((portal: any) => {
            let newPortal: Portal = new Portal(portal.portal);
            newPortal.theme = new Theme(portal.theme);
            portals.push(newPortal);
          });
        }
        return portals;
      })
    );
  }

  updatePortalSettings(portal: Portal, theme: Theme): Observable<Portal> {
    const body = {
      allow_user_communication: portal.allowUserCommunication,
      theme: theme
    }

    const url = this.settings.apiUrl + `portals/${portal.id}/settings`;

    return this.http.put(url, body).pipe(
      map((data: any) => {
        let portal: Portal = new Portal(data.portal);
        portal.theme = new Theme(data.theme);
        return portal;
      })
    );
  }

  updatePortalMedia(portalID: number, isBanner: boolean, base64: string): Observable<string> {
    const body = {
      is_banner: isBanner,
      base64_file: base64
    }

    const url = this.settings.apiUrl + `portals/${portalID}/media`;

    return this.http.put(url, body).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getPortalMember(portalID: number, appUserID: number): Observable<PortalMember> {
    const url = this.settings.apiUrl + `portals/${portalID}/members/${appUserID}`;

    return this.http.get(url).pipe(
      map((data: any) => {
        return new PortalMember(data);
      })
    );
  }

  getPortalMembers(portalID: number, groupID?: number): Observable<Member[]> {
    let optionalGroupID: number;
    if(groupID && groupID != 0) {
      optionalGroupID = groupID;
    }

    const params = {
      group_id: optionalGroupID,
      active: true
    }

    const url = this.settings.apiUrl + `portals/${portalID}/members?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let members: Member[] = [];
        if(data) {
          data.forEach((member: any) => {
            let portalMember = new PortalMember(member.portal_member);
            let appUser = new AppUser(member.app_user);
            let appDevice = new AppDevice(member.last_logged_in_app_device);
            members.push(new Member(appUser, portalMember, appDevice));
          });
        }
        return members;
      })
    );
  }

  getPortalMembersExcludingAliasMembers(portalID: number, excludeAliasID: number): Observable<Member[]> {
    const params = {
      exclude_alias_id: excludeAliasID,
      active: true
    }

    const url = this.settings.apiUrl + `portals/${portalID}/members?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let members: Member[] = [];
        if(data) {
          data.forEach((member: any) => {
            let portalMember = new PortalMember(member.portal_member);
            let appUser = new AppUser(member.app_user);
            let appDevice = new AppDevice(member.last_logged_in_app_device);
            members.push(new Member(appUser, portalMember, appDevice));
          });
        }
        return members;
      })
    );
  }

  getChildPortalMembers(portalID: number, childPortalID: number, invitable?: boolean): Observable<Member[]> {
    const params = {
      invitable: invitable
    }

    const url = this.settings.apiUrl + `portals/${portalID}/children/${childPortalID}/members?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let members: Member[] = [];
        if(data) {
          data.forEach((member: any) => {
            let portalMember = new PortalMember(member.portal_member);
            let appUser = new AppUser(member.app_user);
            let appDevice = new AppDevice(member.last_logged_in_app_device);
            members.push(new Member(appUser, portalMember, appDevice));
          });
        }
        return members;
      })
    );
  }

  getInactivePortalMembers(portalID: number): Observable<Member[]> {
    const params = {
      active: false
    }

    const url = this.settings.apiUrl + `portals/${portalID}/members?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let members: Member[] = [];
        if(data) {
          data.forEach((member: any) => {
            let portalMember = new PortalMember(member.portal_member);
            let appUser = new AppUser(member.app_user);
            let appDevice = new AppDevice(member.last_logged_in_app_device);
            members.push(new Member(appUser, portalMember, appDevice));
          });
        }
        return members;
      })
    );
  }

  getAllPortalMembers(portalID: number): Observable<Member[]> {
    const url = this.settings.apiUrl + `portals/${portalID}/members`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let members: Member[] = [];
        if(data) {
          data.forEach((member: any) => {
            let portalMember = new PortalMember(member.portal_member);
            let appUser = new AppUser(member.app_user);
            let appDevice = new AppDevice(member.last_logged_in_app_device);
            members.push(new Member(appUser, portalMember, appDevice));
          });
        }
        return members;
      })
    );
  }

  renewPortalSubscription(portalID: number, appUserID: number): Observable<[string, PortalMember]>{
    const url = this.settings.apiUrl + `portals/${portalID}/members/${appUserID}/renew`;

    return this.http.put(url, {}).pipe(
      map((data: any) => {
        let respStatus: string = data.status;
        let portalMember = new PortalMember(data.portal_member);
        
        return [
          respStatus,
          portalMember
        ]
      })
    );
  }

  updatePortalMember(childPortalID: number, appUserID: number, portalMember: PortalMember, parentPortalID?: number) {
    const body = {
      portal_member: portalMember,
      parent_portal_id: parentPortalID
    }

    const url = this.settings.apiUrl + `portals/${childPortalID}/members/${appUserID}`;

    return this.http.put(url, body);
  }

  reactivatePortalMember(portalID: number, appUserID) {
    const url = this.settings.apiUrl + `portals/${portalID}/members/${appUserID}/reactivate`;

    return this.http.put(url, {}).pipe(
      map((data: any) => {
        return new PortalMember(data);
      })
    );
  }

  deletePortalMember(portalID: number, appUserID: number): Observable<PortalMember> {
    const url = this.settings.apiUrl + `portals/${portalID}/members/${appUserID}`;

    return this.http.delete(url).pipe(
      map((data: any) => {
        return new PortalMember(data);
      })
    );
  }

  inviteMemberToPortal(portalID: number, portalInvites: PortalInvite[]): Observable<[string, PortalInvite[]]> {
    let invites = [];
    portalInvites.forEach(invite => {
      invites.push({
        'email': invite.email,
        'portal_role_id': invite.portalRoleID,
        'is_staff': invite.isStaff,
        'has_parent_access': invite.hasParentAccess
      })
    });

    const body = {
      'portal_invites': invites
    }

    const url = this.settings.apiUrl + `portals/${portalID}/invites`;

    return this.http.post(url, body).pipe(
      map((data: any) => {
        let respStatus: string = data.status;
        let invites: PortalInvite[] = [];
        if(data?.portal_invites) {
          data.portal_invites.forEach((invite: any) => {
            invites.push(new PortalInvite(invite))
          });
        }
        return [
          respStatus,
          invites
        ]
      })
    );
  }

  acceptPortalInvite(sessionToken: string, portalID: number, email: string, token: string): Observable<PortalInvite> {
    const httpOptions = { // Manually put the sessiontoken into the header.
      headers: {
        'Authorization': sessionToken
      }
    }

    const body = {
      email: email,
      token: token
    }

    const url = this.settings.apiUrl + `portals/${portalID}/invites`;

    return this.httpManual.put(url, body, httpOptions).pipe(
      map((data: any) => {
        return new PortalInvite(data);
      })
    );
  }

  getAllPortalInvites(portalID: number): Observable<PortalInvite[]> {
    const url = this.settings.apiUrl + `portals/${portalID}/invites`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let invites: PortalInvite[] = [];
        data.forEach((invite: any) => {
          invites.push(new PortalInvite(invite))
        });
        return invites;
      })
    );
  }

  getPendingPortalInvites(portalID: number): Observable<PortalInvite[]> {
    const params = {
      pending: true
    }

    const url = this.settings.apiUrl + `portals/${portalID}/invites?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let invites: PortalInvite[] = [];
        data.forEach((invite: any) => {
          invites.push(new PortalInvite(invite))
        });
        return invites;
      })
    );
  }

  revokePortalInvite(portalID: number, email: string): Observable<PortalInvite> {
    const httpOptions = {
      body: {
        email: email
      }
    }

    const url = this.settings.apiUrl + `portals/${portalID}/invites`;

    return this.http.delete(url, httpOptions).pipe(
      map((data: any) => {
        return new PortalInvite(data);
      })
    );
  }

  getPortalRoles(): Observable<PortalRole[]> {
    const url = this.settings.apiUrl + `portalRoles`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let portalRoles: PortalRole[] = [];
        data.forEach((portalRole: any) => {
          portalRoles.push(new PortalRole(portalRole))
        });
        return portalRoles;
      })
    );
  }

  getPortalRoleRules(): Observable<any> {
    const url = this.settings.apiUrl + `rules/portalroles`;

    return this.http.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  addChildPortalMembersToPortal(portalID: number, childPortalMembers: PortalMember[]): Observable<PortalInvite[]> {
    const body = {
      child_portal_member_invites: childPortalMembers
    }

    const url = this.settings.apiUrl + `portals/${portalID}/invites/childPortal`;

    return this.http.post(url, body).pipe(
      map((data: any) => {
        let invites: PortalInvite[] = [];
        if(data?.portal_invites) {
          data.portal_invites.forEach((invite: any) => {
            invites.push(new PortalInvite(invite));
          });
        }
        return invites;
      })
    );
  }
}
